<script>
  import { untrack, tick } from 'svelte'
  import { router, page, useForm } from '@inertiajs/svelte'
  import Drawer from './drawer.svelte'
  import EmptyState from './empty_state.svelte'
  import FilterSelect from './filter_select.svelte'
  import SelectField from './form/select_field.svelte'
  import TextField from './form/text_field.svelte'
  import SubmitButton from './form/submit_button.svelte'
  import LightboxViewer from './lightbox/lightbox_viewer.svelte'
  import { confirm } from './confirm.js'
  import Icon from './ui/icon.svelte'
  import { Button } from './ui'

  // Browses existing Image records through the optional `image_library` shared prop
  // (AdminSite::ImageLibraryProp): a partial reload of the open page with `library_*`
  // params and `preserveUrl`, so the picker works inside any form without a JSON endpoint.
  // The server deep-merges "Load more" pages into the loaded images; a fresh load resets them.
  //
  // Search + place filter live in the drawer's pinned header and the Add action in its
  // pinned footer, so neither scrolls away with a long grid. Selections are kept as full
  // image objects (not ids filtered against the current page) so picks survive searches.
  // `placeId` opens the library on that place's images (the old CMS's picker did), and
  // `attachedIds` marks images the field already holds — clicking one calls `onremove`.
  // Each tile can also be viewed full size, edited (place/title/tags) or archived in place,
  // through media#update/destroy; the list then reloads with the current search.
  let { open = $bindable(false), multiple = false, placeId = null, attachedIds = [], onselect, onremove } = $props()

  let query = $state('')
  let placeFilter = $state('')
  let loading = $state(false)
  let selected = $state([])

  const library = $derived($page.props.image_library ?? { images: [], pagy: { page: 1, pages: 1 } })
  const images = $derived(library.images ?? [])

  const isSelected = (image) => selected.some((s) => s.id === image.id)
  const isAttached = (image) => attachedIds.includes(image.id)

  const load = ({ append = false } = {}) => {
    loading = !append
    router.reload({
      only: ['image_library'],
      reset: append ? [] : ['image_library'],
      data: { library_search: query, library_place_id: placeFilter, library_page: append ? library.pagy.page + 1 : 1 },
      preserveUrl: true,
      preserveState: true,
      preserveScroll: true,
      onFinish: () => (loading = false)
    })
  }

  // Only `open` drives this; untracked so typing a search doesn't re-run the reset.
  $effect(() => {
    if (!open) return
    untrack(() => {
      query = ''
      placeFilter = placeId || ''
      selected = []
      load()
    })
  })

  let timeoutId = null
  const onSearchInput = () => {
    if (timeoutId) clearTimeout(timeoutId)
    timeoutId = setTimeout(() => load(), 300)
  }

  const onPlaceChange = (value) => {
    placeFilter = value || ''
    load()
  }

  const toggle = (image) => {
    if (isAttached(image)) {
      onremove?.(image)
      return
    }

    if (!multiple) {
      onselect?.([image])
      open = false
      return
    }

    selected = isSelected(image) ? selected.filter((s) => s.id !== image.id) : [...selected, image]
  }

  let lightboxOpen = $state(false)
  let lightboxIndex = $state(0)
  const lightboxItems = $derived(images.map((image) => ({ type: 'image', url: image.url, title: image.title })))
  const view = (index) => {
    lightboxIndex = index
    lightboxOpen = true
  }

  let editing = $state(null)
  // Checked live against media#update (Precognition) before Save.
  const editForm = useForm({ image: { title: '', all_tags: '', place_id: '' } }).withPrecognition('patch', () => `/media/${editing?.id}`)

  const startEdit = (image) => {
    editing = image
    $editForm.image = { title: image.title || '', all_tags: (image.all_tags || []).join(', '), place_id: image.place_id || '' }
    $editForm.clearErrors()
  }

  const submitEdit = (event) => {
    event.preventDefault()
    $editForm.patch(`/media/${editing.id}`, {
      only: ['flash'],
      preserveState: true,
      preserveScroll: true,
      onSuccess: () => {
        editing = null
        load()
      }
    })
  }

  const archive = async (image) => {
    const ok = await confirm({
      title: 'Confirm Archive',
      variant: 'error',
      body: "Archiving an image will remove it from any campaigns, feeds, places, or adventures it's part of. This action is permanent unless reversed by a developer. Are you sure?",
      confirmLabel: 'Archive'
    })
    if (!ok) return

    router.delete(`/media/${image.id}`, {
      only: ['flash'],
      preserveState: true,
      preserveScroll: true,
      onSuccess: () => {
        if (isAttached(image)) onremove?.(image)
        selected = selected.filter((s) => s.id !== image.id)
        load()
      }
    })
  }

  const confirmSelection = () => {
    onselect?.(selected)
    selected = []
    open = false
  }
</script>

{#snippet selectionFooter()}
  <div class="flex items-center justify-between gap-2">
    <div class="flex items-center gap-2 text-sm text-base-content/60">
      <span>{selected.length} selected</span>
      {#if selected.length > 0}
        <Button variant="ghost" size="xs" onclick={() => (selected = [])}>Clear</Button>
      {/if}
    </div>
    <Button variant="primary" size="sm" disabled={selected.length === 0} onclick={confirmSelection}>
      {selected.length > 0 ? `Add ${selected.length} selected` : 'Add selected'}
    </Button>
  </div>
{/snippet}

<Drawer bind:open title={editing ? 'Edit image' : 'Image library'} width="w-[36rem]" footer={multiple && !editing ? selectionFooter : undefined}>
  {#snippet header()}
    <div class="flex items-end gap-2">
      <div class="relative grow">
        <input
          type="search"
          bind:value={query}
          oninput={onSearchInput}
          placeholder="Search images…"
          aria-label="Search images"
          class="input input-sm w-full pr-7 [&::-webkit-search-cancel-button]:hidden"
        />
        {#if query}
          <button
            type="button"
            class="absolute right-1.5 top-1/2 -translate-y-1/2 text-base-content/40 hover:text-base-content cursor-pointer"
            aria-label="Clear search"
            onclick={() => {
              query = ''
              load()
            }}
          >
            <Icon name="close" size="xs" />
          </button>
        {/if}
      </div>
      <FilterSelect
        label="Place"
        value={placeFilter}
        placeholder="All places"
        source="places"
        sourceParams={{ area_id: $page.props.current_area_id, with_area: 1 }}
        onchange={onPlaceChange}
      />
    </div>
    {#if attachedIds.length > 0}
      <p class="text-xs text-base-content/60 mt-2">Images already added are marked — click one to remove it.</p>
    {/if}
  {/snippet}

  {#if editing}
    <form novalidate onsubmit={submitEdit} class="space-y-4">
      <img src={editing.url} alt={editing.title || 'Image'} class="w-full h-48 object-contain bg-base-200 rounded-box" />
      <SelectField
        bind:value={$editForm.image.place_id}
        label="Place"
        name="place_id"
        source="places"
        sourceParams={{ area_id: $page.props.current_area_id, with_area: 1 }}
        placeholder="No place"
        onchange={() => tick().then(() => $editForm.validate('image.place_id'))}
        error={$editForm.errors['image.place_id']}
      />
      <div onchange={() => $editForm.validate('image.title')}>
        <TextField bind:value={$editForm.image.title} label="Image Title" name="title" error={$editForm.errors['image.title']} />
      </div>
      <div onchange={() => $editForm.validate('image.all_tags')}>
        <TextField bind:value={$editForm.image.all_tags} label="Image Tags" name="all_tags" help="Comma separated" error={$editForm.errors['image.all_tags']} />
      </div>
      <div class="flex justify-end gap-2">
        <Button variant="ghost" size="sm" onclick={() => (editing = null)}>Back to library</Button>
        <SubmitButton processing={$editForm.processing} label="Save" full={false} />
      </div>
    </form>
  {:else if loading}
    <div class="grid grid-cols-4 gap-2">
      {#each Array(12) as _, index (index)}
        <div class="skeleton aspect-square w-full rounded-box"></div>
      {/each}
    </div>
  {:else if images.length === 0}
    <EmptyState
      icon="media"
      title="No images found"
      description={query || placeFilter ? 'Try a different search or place.' : 'Upload images to the media library first.'}
    />
  {:else}
    <div class="grid grid-cols-4 gap-2">
      {#each images as image, index (image.id)}
        {@const chosen = isSelected(image)}
        {@const attached = isAttached(image)}
        <div class="group relative">
          <button
            type="button"
            class="group relative aspect-square rounded-box overflow-hidden bg-base-200 focus-visible:outline-2 focus-visible:outline-primary {chosen
              ? 'ring-2 ring-primary ring-offset-2 ring-offset-base-100'
              : ''} {attached ? 'ring-2 ring-success ring-offset-2 ring-offset-base-100' : ''}"
            title={attached ? `${image.title || 'Image'} (added — click to remove)` : image.title || ''}
            aria-pressed={multiple || attached ? chosen || attached : undefined}
            onclick={() => toggle(image)}
          >
            <img
              src={image.url}
              alt={image.title || 'Library image'}
              class="h-full w-full object-contain transition-opacity {chosen || attached ? 'opacity-80' : 'group-hover:opacity-85'}"
            />
            {#if chosen || attached}
              <span
                class="absolute top-1.5 right-1.5 flex h-5 w-5 items-center justify-center rounded-full {attached
                  ? 'bg-success text-success-content'
                  : 'bg-primary text-primary-content'}"
              >
                <Icon name="check" size="xs" />
              </span>
            {/if}
          </button>
          <div class="absolute bottom-1 left-1 flex gap-0.5 rounded-box bg-base-100/90 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
            <Button variant="ghost" size="xs" square icon="view" aria-label="View full size" onclick={() => view(index)} />
            <Button variant="ghost" size="xs" square icon="edit" aria-label="Edit image" onclick={() => startEdit(image)} />
            <Button variant="ghost" size="xs" square icon="delete" aria-label="Archive image" class="text-error" onclick={() => archive(image)} />
          </div>
        </div>
      {/each}
    </div>
    {#if library.pagy.page < library.pagy.pages}
      <div class="flex justify-center pt-3">
        <Button variant="ghost" size="sm" onclick={() => load({ append: true })}>Load more</Button>
      </div>
    {/if}
  {/if}
</Drawer>

<LightboxViewer bind:open={lightboxOpen} items={lightboxItems} bind:index={lightboxIndex} />
